import './LoginIllustration.styl';

// Syncra base palette (see $syncra in assets/styles/base/__config.styl).
const INK = '#0b1d3a';
const PRIMARY = '#00b4c3';
const SUCCESS = '#00a98f';
const WARN = '#ff7a00';
const BG = '#eef1f3';

/** Parallel wavy lines: `count` copies of a cubic curve, each shifted by `step`. */
function Waves({ d, count, step, color, opacity }: { d: string; count: number; step: [number, number]; color: string; opacity: number }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.4" opacity={opacity}>
      {Array.from({ length: count }, (_, i) => (
        <path key={i} d={d} transform={`translate(${i * step[0]} ${i * step[1]})`} />
      ))}
    </g>
  );
}

/** Concentric rings around one centre. */
function Rings({ cx, cy, radii, color, opacity, width = 1.5 }: { cx: number; cy: number; radii: number[]; color: string; opacity: number; width?: number }) {
  return (
    <g fill="none" stroke={color} strokeWidth={width} opacity={opacity}>
      {radii.map((r) => (
        <circle key={r} cx={cx} cy={cy} r={r} />
      ))}
    </g>
  );
}

/**
 * Decorative background of the auth screens: line work and circles in the Syncra palette on a
 * light grey-white base, kept to the edges so the centre (behind the card) stays calm. Fills are
 * limited to small dots and two faint discs.
 */
export function LoginIllustration() {
  return (
    <svg className="f-login-scene" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <rect width="1600" height="900" fill={BG} />

      {/* faint discs for depth */}
      <circle cx="140" cy="140" r="170" fill={PRIMARY} opacity=".07" />
      <circle cx="1480" cy="780" r="210" fill={SUCCESS} opacity=".07" />

      {/* top-left: concentric rings and an orange arc */}
      <Rings cx={140} cy={140} radii={[60, 95, 130, 170]} color={INK} opacity={0.28} />
      <circle cx="140" cy="140" r="210" fill="none" stroke={PRIMARY} strokeWidth="2.5" strokeDasharray="4 10" strokeLinecap="round" opacity=".8" />
      <path d="M300 60 A230 230 0 0 1 360 250" fill="none" stroke={WARN} strokeWidth="3" strokeLinecap="round" />

      {/* left: wave bundle */}
      <Waves d="M-40 470 C120 420 240 560 400 520 S620 380 760 430" count={7} step={[0, 12]} color={INK} opacity={0.2} />

      {/* bottom-left: primary rings and outline circles */}
      <Rings cx={260} cy={820} radii={[70, 110]} color={PRIMARY} opacity={0.75} width={2} />
      <circle cx="470" cy="740" r="34" fill="none" stroke={WARN} strokeWidth="3" />
      <circle cx="90" cy="640" r="18" fill="none" stroke={SUCCESS} strokeWidth="2.5" />

      {/* top-right: big rings, dashed orbit, primary arc */}
      <Rings cx={1470} cy={110} radii={[80, 120, 160, 200, 240]} color={INK} opacity={0.2} />
      <path d="M1230 110 A240 240 0 0 1 1470 -130" fill="none" stroke={PRIMARY} strokeWidth="3" strokeLinecap="round" transform="rotate(180 1470 110)" />
      <circle cx="1470" cy="110" r="290" fill="none" stroke={SUCCESS} strokeWidth="2" strokeDasharray="2 12" strokeLinecap="round" opacity=".7" />

      {/* right: wave bundle */}
      <Waves d="M840 300 C980 250 1080 380 1230 350 S1480 230 1660 280" count={6} step={[0, 13]} color={PRIMARY} opacity={0.35} />

      {/* bottom-right: rings and an orange arc */}
      <Rings cx={1480} cy={780} radii={[60, 100, 140]} color={INK} opacity={0.25} />
      <path d="M1180 820 A200 200 0 0 1 1360 600" fill="none" stroke={WARN} strokeWidth="3" strokeLinecap="round" />
      <Waves d="M900 900 C1000 820 1120 860 1230 800" count={5} step={[14, 0]} color={INK} opacity={0.18} />

      {/* scattered dots: the only solid fills */}
      <g>
        <circle cx="420" cy="140" r="7" fill={WARN} />
        <circle cx="455" cy="175" r="4" fill={PRIMARY} />
        <circle cx="60" cy="330" r="5" fill={INK} opacity=".5" />
        <circle cx="560" cy="860" r="6" fill={PRIMARY} />
        <circle cx="1210" cy="60" r="6" fill={WARN} />
        <circle cx="1180" cy="100" r="4" fill={INK} opacity=".5" />
        <circle cx="1550" cy="460" r="7" fill={PRIMARY} />
        <circle cx="1300" cy="560" r="5" fill={SUCCESS} />
        <circle cx="1080" cy="760" r="4" fill={INK} opacity=".5" />
      </g>
    </svg>
  );
}
